import React from 'react';
import Slider from 'components/commercetools-ui/atoms/slider';
import Typography from 'components/commercetools-ui/atoms/typography';
import Image from 'frontastic/lib/image';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { tablet } from 'helpers/utils/screensizes';
import { Tiempos, NationalBook } from 'fonts';
import { GenericSectionContent } from '../../types';
import { useAdditionalCss } from '../../../helpers/useAdditionalCss';
import { sanitizeHtml, SectionCtas, SECTION_SPACING } from '../../../helpers/rendering';

// Hero Banner ← Methven `section-with-banner-slider` / `hero`: full-bleed autoplaying banner carousel.
// Each card is a slide: bg image (darkened) + overlay heading/description/CTAs (desktop) or a black
// text panel below the image (mobile).
//
/*
  Hook classes, for the section's own `additional_css` to override. The defaults below are the home
  page banner; every page that wants something else says so in its entry.

  hero-banner-media            - the fixed-height box the image fills. Set `height` on it.
  hero-banner-image            - the image itself. `filter: brightness(…)`, `object-position: …`.
  hero-banner-content          - the desktop overlay box, so also where the copy sits on the image:
                                 `left`, `top`, `padding-left`.
  hero-banner-title-width      - the heading's wrapper. Set `max-width` on it.
  hero-banner-title-font-size  - the heading itself. Set `font-size` on it.
  hero-banner-description      - the description. Set `width` on it.

  The last three exist ONLY on the desktop overlay. Below 800px the banner is a black text panel
  whose elements carry none of them, which is what makes a width, size or position rule desktop-only
  without needing a media query.

  A rule written against these beats the Tailwind utility on the same element: scoped, the selector
  is two classes (0-2-0) against the utility's one (0-1-0). `sanitizeCss` keeps `filter`, `height`,
  `width`, `max-width`, `font-size` and `@media`; it strips only comments, tags, `expression(`,
  `javascript:`, `@import`, `-moz-binding` and remote `url()`.
*/
export default function HeroBannerRendering({ content }: { content: GenericSectionContent }) {
  const { cards = [], additional_css, additional_css_class_names } = content;
  const { uniqueClass, scopedAdditionalCss } = useAdditionalCss(additional_css, 'HeroBannerRendering');
  const [isTabletSize] = useMediaQuery(tablet);

  return (
    <>
      {scopedAdditionalCss && <style dangerouslySetInnerHTML={{ __html: scopedAdditionalCss }} />}
      {/*
        Every slide takes the height of the tallest one, and its text panel fills that height.

        Swiper gives each `.swiper-slide` its own content height, so below 800px -- where the copy
        sits in a black panel under the image rather than over it -- a short slide's panel stopped
        24px above a long one's, and the white strip under it moved as the carousel advanced. The
        section itself never resized (the flex wrapper was already as tall as its tallest child);
        the panel just failed to reach the bottom.

        `h-auto` is what lets the slide stretch to the wrapper -- a percentage height cannot --
        and the two flex columns below pass that height down to the panel.
      */}
      <div className={`${SECTION_SPACING} hero-slider-container relative w-full [&_.swiper-slide]:flex [&_.swiper-slide]:h-auto [&_.swiper-slide]:flex-col ${[additional_css_class_names, uniqueClass].filter(Boolean).join(' ')}`}>
        <Slider
          slidesPerView={1}
          dots
          arrows={isTabletSize}
          innerArrows
          allowArrowsOnTouchDevice
          allowTouchMove
          prevButtonStyles={{ padding: '20px', left: '0px' }}
          nextButtonStyles={{ padding: '20px', right: '0px' }}
          autoplay={{ delay: 5000, disableOnInteraction: false }}
        >
          {cards.map((card, index) => (
            <div key={index} className="flex flex-auto flex-col">
              <div className="hero-banner-media relative h-[18rem] md:h-[34rem] 2xl:h-[45rem]">
                {card?.desktop_image?.url && (
                  <Image priority loading="eager" src={card.desktop_image.url} alt={card.desktop_image.description ?? card?.title ?? ''} fill style={{ objectFit: 'cover' }} className="hero-banner-image brightness-75" sizes="100vw" />
                )}
              </div>
              {isTabletSize ? (
                <div className="hero-banner-content absolute top-1/2 -translate-y-1/2 pl-128 text-left xl:left-0">
                  {card?.title && (
                    <div className="hero-banner-title-width max-w-[14rem] xl:max-w-[20rem] 2xl:max-w-[24rem]">
                      <Typography as="h2" className={`hero-banner-title-font-size text-left text-34 font-semibold text-[#FFF] drop-shadow-sm md:leading-[3.8rem] xl:text-48 xl:leading-[4.2rem] 2xl:text-62 ${Tiempos.className}`}>
                        {card.title}
                      </Typography>
                    </div>
                  )}
                  {card?.description && (
                    <div className={`hero-banner-description mt-22 w-[60%] text-left text-18 font-medium leading-8 tracking-wide text-white 2xl:text-20 ${NationalBook.className}`} dangerouslySetInnerHTML={{ __html: sanitizeHtml(card.description) }} />
                  )}
                  {card?.ctas && card.ctas.length > 0 && <SectionCtas ctas={card.ctas} className="mt-22" fullWidthUntil="lg" />}
                </div>
              ) : (
                <div className="flex-auto bg-black px-24 pb-40 pt-30 sm:pb-63 sm:pt-40">
                  {card?.title && (
                    <Typography as="h2" className="text-left text-34 font-semibold leading-tight text-white">{card.title}</Typography>
                  )}
                  {card?.description && (
                    <div className="mt-22 text-left text-16 text-white md:text-20" dangerouslySetInnerHTML={{ __html: sanitizeHtml(card.description) }} />
                  )}
                  {card?.ctas && card.ctas.length > 0 && <SectionCtas ctas={card.ctas} className="mt-22" fullWidthUntil="lg" />}
                </div>
              )}
            </div>
          ))}
        </Slider>
      </div>
    </>
  );
}
